import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.#variantMediaData = this.#readVariantMediaData();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleVariantMediaSelection, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #variantMediaData = null;

  #readVariantMediaData() {
    const mapping = this.querySelector('script[data-pp-variant-media]');
    try {
      return mapping?.textContent ? JSON.parse(mapping.textContent) : null;
    } catch {
      return null;
    }
  }

  #handleVariantMediaSelection = (event) => {
    if (event.target !== this.slideshow || event.detail?.userInitiated !== true) return;
    const data = this.#variantMediaData ?? this.#readVariantMediaData();
    if (!data || !Array.isArray(data.optionNames) || !Array.isArray(data.variants)) return;
    const mediaId = data.mediaIds?.[event.detail.index];
    if (!mediaId) return;

    const designIndex = data.optionNames.findIndex((name) =>
      ['design', 'design number', 'pattern', '图案', '款式'].includes(String(name).trim().toLowerCase())
    );
    if (designIndex < 0) return;

    const scope = this.closest('.shopify-section');
    if (!scope || this.closest('dialog, product-card, quick-add-dialog')) return;
    const picker = Array.from(scope.querySelectorAll('variant-picker')).find((element) =>
      element.dataset.productId === String(data.productId) &&
      !element.closest('dialog, product-card, quick-add-dialog')
    );
    if (!picker) return;

    const selections = data.optionNames.map((name) => {
      const input = Array.from(picker.querySelectorAll('input[data-option-name]:checked')).find(
        (element) => element.dataset.optionName === name
      );
      if (input) return input.value;
      const select = Array.from(picker.querySelectorAll('select')).find((element) =>
        Array.from(element.options).some((option) => option.dataset.optionName === name)
      );
      return select?.value;
    });
    if (selections.some((value) => value === undefined)) return;

    const matches = data.variants.filter((variant) =>
      String(variant.mediaId) === String(mediaId) &&
      Array.isArray(variant.options) &&
      variant.options.length === selections.length &&
      variant.options.every((value, index) => index === designIndex || value === selections[index])
    );
    const designs = [...new Set(matches.map((variant) => variant.options[designIndex]))];
    if (designs.length !== 1 || designs[0] === selections[designIndex]) return;

    const designName = data.optionNames[designIndex];
    const radio = Array.from(picker.querySelectorAll('input[data-option-name]')).find((element) =>
      element.dataset.optionName === designName && element.value === designs[0]
    );
    if (radio) {
      if (radio.disabled) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const select = Array.from(picker.querySelectorAll('select')).find((element) =>
      Array.from(element.options).some((option) =>
        option.dataset.optionName === designName && option.value === designs[0] && !option.disabled
      )
    );
    if (!select || select.disabled) return;
    select.value = designs[0];
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
